import { useEffect, useState } from 'react'
import { useAuth } from '@structural-climbing/auth'

export interface RouteComment {
  id: string
  user_id: string
  body: string
  created_at: string
  updated_at: string
  profile: {
    first_name: string | null
    last_name: string | null
    nickname: string | null
    email: string | null
    avatar_ext: string | null
    avatar_updated_at: string | null
  } | null
  /** How the climber did on the route: 1 is a flash. Null when they did not say. */
  attempts: number | null
  rating: number | null
}

interface State {
  comments: RouteComment[]
  loading: boolean
  error: boolean
}

/**
 * Climbers' notes on one route, for the setters. Loads while `routeId` is set,
 * so a closed panel costs nothing.
 */
export function useRouteComments(apiUrl: string, routeId: string | null): State {
  const { getAccessTokenSilently } = useAuth()
  const [state, setState] = useState<State>({ comments: [], loading: false, error: false })

  useEffect(() => {
    if (!routeId) {
      setState({ comments: [], loading: false, error: false })
      return
    }
    let cancelled = false
    setState({ comments: [], loading: true, error: false })
    void (async () => {
      try {
        const token = await getAccessTokenSilently()
        const res = await fetch(`${apiUrl}/climbing-routes/${routeId}/comments`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (!res.ok) throw new Error(String(res.status))
        const data = (await res.json()) as { comments: RouteComment[] }
        if (!cancelled) setState({ comments: data.comments, loading: false, error: false })
      } catch {
        if (!cancelled) setState({ comments: [], loading: false, error: true })
      }
    })()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl, routeId])

  return state
}
